// @givmo/compliance — Compliance settings' "Givmo’s filing desk" (CCM-R1-DESK-WEB; REVIEW-35's
// desk-23 to desk-25, D-CCM-83's Q6): the organization's agreement for Givmo to file, as the
// standing read serves it, and turning it off. Agreeing is Givmo's ceremony with the organization
// (D-CCM-70), never a control here, so the card shows only once an agreement was recorded.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, testIdProp } from '@givmo/design-system';
import { useCompliance } from './compliance-context';
import { isModuleUnavailable, serverMessage } from './compliance-decode';
import { decodeDeskStanding } from './compliance-desk-model';
import { deskStandingQueryKey, getDeskStanding } from './compliance-queries';
import { shortDateIn } from './compliance-signing-model';
import { N12, N15_BODY, N15_TITLE, RUN_BY_AI, TURN_OFF, n14, n16 } from './desk-words';
import { DeskDialog } from './filing-desk';

const PREFIX = 'compliance-desk-standing';
/** The landed failure line, where the server gives none. */
const TRY_AGAIN_LINE = 'Please try again.';

export interface DeskStandingCardProps {
  /** Whether a change is in flight, for the host page's unsaved-changes guard. */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * N14 while the agreement is in force, with D-CCM-67's sentence and "Turn off" for whoever may turn
 * it off (`can_revoke`), which asks first (N15); N16 once it is off. Nothing where none was ever
 * recorded, or where the desk is dark.
 */
export function DeskStandingCard({ onDirtyChange }: DeskStandingCardProps) {
  const { orgRef, isReady, operations, moduleStanding } = useCompliance();
  const queryClient = useQueryClient();
  const available = isReady && moduleStanding === 'available' && orgRef !== null;
  const queryKey = deskStandingQueryKey(orgRef);
  const read = useQuery({
    queryKey,
    queryFn: () => getDeskStanding(operations, orgRef ?? ''),
    enabled: available,
  });
  const [asking, setAsking] = useState(false);
  const turnOff = useMutation({
    mutationFn: () => operations.revokeDeskStanding(orgRef ?? ''),
    onSuccess: (answer) => {
      queryClient.setQueryData(queryKey, answer);
      // Givmo stopped working on every filing it had and hadn't sent (R5); a filing it sent stays
      // with it until its confirmation (W6, D-CCM-92). Each reads again.
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
      setAsking(false);
    },
  });
  const inFlight = turnOff.isPending;
  useEffect(() => {
    onDirtyChange?.(inFlight);
  }, [inFlight, onDirtyChange]);

  if (!available) return null;
  if (read.error != null && isModuleUnavailable(read.error)) return null;
  const standingRead = decodeDeskStanding(read.data);
  const standing = standingRead?.standing;
  if (standing === undefined) return null;
  const inForce = standing.state === 'active';
  if (!inForce && standing.state !== 'revoked') return null;
  const line = inForce
    ? n14(shortDateIn(standing.recordedAt))
    : n16(shortDateIn(standing.revokedAt));

  return (
    <Card testId={PREFIX}>
      <div className="flex flex-wrap items-start justify-between gap-x-24 gap-y-12 px-20 py-16">
        <div className="min-w-0 flex-1 basis-[320px]">
          <h3 className="text-s16 font-w600 text-text-strong">{N12}</h3>
          <p
            className="mt-2 max-w-[640px] text-s13 leading-h15 text-balance text-text-secondary"
            {...testIdProp(`${PREFIX}-line`)}
          >
            {line}
          </p>
          {inForce && (
            <p className="mt-2 max-w-[640px] text-s13 leading-h15 text-balance text-text-tertiary">
              {RUN_BY_AI}
            </p>
          )}
        </div>
        {inForce && standingRead?.canRevoke === true && (
          <div className="flex shrink-0 items-center pt-2">
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => setAsking(true)}
              testId={`${PREFIX}-turn-off`}
            >
              {TURN_OFF}
            </Button>
          </div>
        )}
      </div>
      <DeskDialog
        open={asking}
        title={N15_TITLE}
        lines={[N15_BODY]}
        confirm={TURN_OFF}
        pending={inFlight}
        failure={turnOff.isError ? serverMessage(turnOff.error, TRY_AGAIN_LINE) : undefined}
        onConfirm={() => turnOff.mutate()}
        onClose={() => {
          turnOff.reset();
          setAsking(false);
        }}
        testId={`${PREFIX}-turn-off-dialog`}
      />
    </Card>
  );
}
